/**
 * The rows of the Users permission grid for a nav tree: one per page, a group's children flattened one level
 * (LT-3337), except a group in groupsAsOne, which is one row under its own key and label.
 */
export const grantRowsFromNav = <T extends { key: string; label?: unknown; children?: T[] }>(
    items: T[],
    groupsAsOne: ReadonlySet<string>,
): Omit<T, 'children'>[] =>
    items.flatMap((item) => {
        const { children, ...group } = item;
        if (children?.length && !groupsAsOne.has(item.key)) return children.map(({ children: _deeper, ...child }) => child);
        return [group];
    });

/**
 * Which nav submenu owns a given path.
 *
 * Kept in its own module so it is reachable from tests: Navbar.tsx cannot be
 * imported under Jest at all, because it pulls in src/assets/dark-theme, which
 * imports an SVG whose filename contains parentheses and trips the CRA asset
 * transform.
 */

/**
 * Groups whose children do NOT share the group's own key as their first path
 * segment.
 *
 * navbarContainer derives the open submenu from the first path segment, which
 * holds for general_admin/*, controls_admin/*, tools/* and ml/*. The Monitor
 * group is the exception: its key is `live_historical_data`, but two of its
 * pages sit under a `live_data` prefix, so a deep link or hard refresh onto
 * /live_data/scada2 opened `live_data` — a key matching no submenu — and drew
 * the group collapsed with the selected item hidden inside it.
 *
 * A prefix belongs here only when it differs from its group key. Keep it a
 * map rather than a copy of the nav tree: duplicating the tree is what makes
 * the two drift.
 */
const GROUP_KEY_BY_PATH_PREFIX: Readonly<Record<string, string>> = {
    live_data: 'live_historical_data',
};

/** Every group key the nav renders, plus the ungrouped top-level pages. */
export const KNOWN_MENU_ROOTS: ReadonlySet<string> = new Set<string>([
    'dashboard',
    'oee-console',
    'material_tracking',
    'profile',
    'general_admin',
    'live_historical_data',
    'controls_admin',
    'tools',
    'ml',
]);

/**
 * The submenu key that must be open for `pathname`'s nav item to be visible.
 * Falls back to `fallback` for the site root.
 */
export const navGroupForPath = (pathname: string, fallback: string): string => {
    const prefix = pathname.split('/')[1] || fallback;
    return GROUP_KEY_BY_PATH_PREFIX[prefix] ?? prefix;
};
